import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { useGenesisState, ChatMessageRole, ChatChannel } from '../hooks/useGenesisState'
import { GlassOverlay } from './GlassOverlay'
import { useVoiceChat } from '../hooks/useVoiceChat'
import AetherAudio from './AetherAudio'
import { tatianaVoice } from '../systems/TatianaVoice'
import { WorldSeed, ENVIRONMENT_COLORS } from '../types/world'
import { getGameColor, isScenarioRequest, generateDirectorResponse, buildVanguardGatePrompt, parseVanguardGateResponse, processTatianaMessageStreaming, VanguardGateToolCall, parseToolCall, stripJsonFromText } from '../systems/TatianaAI'
import { parseUserIntent, formatWorldSeedForTerminal } from '../systems/ScenarioParser'
import { auditChain } from '../systems/AuditChain'
import { libp2pMesh, type LobbyMessage } from '../systems/Libp2pMesh'

interface ChatMessageData {
  role: ChatMessageRole
  content: string
  highlightTimeFracture?: boolean
  hasJsonTool?: boolean
  worldSeed?: WorldSeed
}

interface GenesisChatProps {
  visible?: boolean
  messages?: ChatMessageData[]
  isThinking?: boolean
}

// Email capture state for Vanguard Gate
interface WaitlistState {
  isActive: boolean
  message: string
}

// Email capture UI component
const WaitlistEmailCapture: React.FC<{
  message: string
  onSubmit: (email: string) => void
}> = ({ message, onSubmit }) => {
  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.trim() || isSubmitting) return
    
    setIsSubmitting(true)
    try {
      await onSubmit(email.trim())
      setIsSubmitted(true)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isSubmitted) {
    return (
      <div style={{
        padding: '16px',
        background: 'rgba(0, 255, 136, 0.1)',
        border: '1px solid rgba(0, 255, 136, 0.3)',
        borderRadius: '4px',
        marginTop: '12px',
        textAlign: 'center'
      }}>
        <div style={{
          color: '#00FF88',
          fontFamily: 'JetBrains Mono, monospace',
          fontSize: '11px',
          letterSpacing: '0.1em'
        }}>
          [ COORDINATES RECEIVED ]
        </div>
        <div style={{
          color: 'rgba(255, 255, 255, 0.6)',
          fontSize: '10px',
          marginTop: '8px'
        }}>
          You will be contacted when the void opens.
        </div>
      </div>
    )
  }

  return (
    <div style={{
      padding: '16px',
      background: 'rgba(255, 0, 68, 0.1)',
      border: '1px solid rgba(255, 0, 68, 0.3)',
      borderRadius: '4px',
      marginTop: '12px'
    }}>
      <div style={{
        color: '#FF0044',
        fontFamily: 'JetBrains Mono, monospace',
        fontSize: '11px',
        letterSpacing: '0.1em',
        marginBottom: '12px'
      }}>
        [ {message} ]
      </div>
      <form onSubmit={handleSubmit} style={{ display: 'flex', gap: '8px' }}>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com"
          disabled={isSubmitting}
          style={{
            flex: 1,
            background: 'rgba(0, 0, 0, 0.5)',
            border: '1px solid rgba(255, 0, 68, 0.5)',
            borderRadius: '2px',
            padding: '8px 12px',
            color: '#00FFFF',
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: '11px',
            outline: 'none'
          }}
        />
        <button
          type="submit"
          disabled={isSubmitting || !email.trim()}
          style={{
            background: 'rgba(255, 0, 68, 0.2)',
            border: '1px solid rgba(255, 0, 68, 0.5)',
            borderRadius: '2px',
            padding: '8px 16px',
            color: '#FF0044',
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: '10px',
            letterSpacing: '0.1em',
            cursor: isSubmitting ? 'not-allowed' : 'pointer',
            opacity: isSubmitting ? 0.5 : 1
          }}
        >
          {isSubmitting ? '...' : 'SUBMIT'}
        </button>
      </form>
    </div>
  )
}

// Generate ZK audit chain timestamp hash
const generateTimestampHash = (): string => {
  const root = auditChain.getChainRoot()
  const seq = auditChain.getSeq()
  return `0x${root.slice(0, 8)}... | ZK Block #${seq}`
}

// Blinking cursor component for processing state
const BlinkingCursor: React.FC = () => {
  const [visible, setVisible] = useState(true)
  
  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(v => !v)
    }, 500)
    return () => clearInterval(interval)
  }, [])
  
  return (
    <span style={{ opacity: visible ? 1 : 0.3 }}>
      [ ... ]
    </span>
  )
}

// WorldSeed display component for the uplink
const WorldSeedDisplay: React.FC<{ seed: WorldSeed }> = ({ seed }) => {
  const envColor = ENVIRONMENT_COLORS[seed.environment] || '#8A2BE2'
  
  return (
    <div style={{
      marginTop: '8px',
      padding: '8px',
      background: 'rgba(0, 0, 0, 0.3)',
      borderLeft: `2px solid ${envColor}`,
      fontSize: '10px',
      fontFamily: 'JetBrains Mono, monospace',
      color: 'rgba(0, 255, 255, 0.7)'
    }}>
      <div style={{ color: envColor, marginBottom: '4px' }}>
        [ WORLD SEED ]
      </div>
      <div>ENV: {seed.environment.toUpperCase()}</div>
      <div>TIME: {seed.time_of_day.toUpperCase()}</div>
      <div>DIFF: {(seed.difficulty * 100).toFixed(0)}%</div>
      {seed.tactical_focus && (
        <div>FOCUS: {seed.tactical_focus.toUpperCase()}</div>
      )}
      {seed.enemy_density && (
        <div>DENSITY: {seed.enemy_density.toUpperCase()}</div>
      )}
    </div>
  )
}

export const GenesisChat: React.FC<GenesisChatProps> = ({
  visible = true,
  messages: externalMessages,
  isThinking: externalIsThinking
}) => {
  const [input, setInput] = useState('')
  const [timestamp, setTimestamp] = useState(generateTimestampHash())
  const [waitlistState, setWaitlistState] = useState<WaitlistState>({ isActive: false, message: '' })
  const [isGatePassed, setIsGatePassed] = useState(false)
  const [streamingContent, setStreamingContent] = useState('')
  const [replyingTo, setReplyingTo] = useState<string | null>(null)
  const [nodeId, setNodeId] = useState<string>('')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  
  const {
    chatMessages: internalMessages,
    addChatMessage,
    isTatianaTyping: internalIsTyping,
    setTatianaTyping,
    isAiProcessing,
    spawnPortal,
    setPortalOpen,
    worldSeed,
    addSystemMessage,
    setAiProcessing,
    neuralLoadProgress,
    isNeuralReady,
    neuralLoadStatus,
    audioEnabled,
    toggleAudio,
    chatChannel,
    setChatChannel,
    lobbyMessages,
    setLobbyMessages,
    addLobbyMessage,
    lobbyConnected,
    setLobbyConnected,
    lobbyNodeName,
  } = useGenesisState()
  
  const messages = externalMessages || internalMessages
  const isThinking = externalIsThinking !== undefined ? externalIsThinking : internalIsTyping
  
  // Voice chat hook for TTS + STT
  const {
    isListening,
    isSpeaking,
    transcript,
    toggleListening,
    speak,
    stopSpeaking
  } = useVoiceChat()
  
  // Handle transcript updates from speech recognition
  useEffect(() => {
    if (transcript && transcript.trim()) {
      setInput(prev => prev + transcript)
    }
  }, [transcript])
  
  // Load node identity on mount
  useEffect(() => {
    auditChain.getNodeId().then(id => setNodeId(id)).catch(() => {})
  }, [])
  
  // libp2p GossipSub connection for lobby
  useEffect(() => {
    if (chatChannel !== 'lobby') {
      void libp2pMesh.disconnect()
      return
    }

    void libp2pMesh.connect(nodeId, lobbyNodeName)
    setLobbyConnected(libp2pMesh.isConnected())

    const unsubscribe = libp2pMesh.subscribe((msg: LobbyMessage) => {
      addLobbyMessage(msg)
      setLobbyConnected(true)
    })

    const unsubStatus = libp2pMesh.onStatus((connected) => {
      setLobbyConnected(connected)
    })

    return () => {
      unsubscribe()
      unsubStatus()
      void libp2pMesh.disconnect()
    }
  }, [chatChannel, lobbyNodeName])
  
  // Speak Tatiana's response when streaming completes — uses TatianaVoice singleton
  const speakResponse = useCallback((text: string) => {
    if (text && text.trim() && audioEnabled) {
      tatianaVoice.speak(text)
    }
  }, [audioEnabled])
  
  // Handle waitlist email submission
  const handleWaitlistSubmit = useCallback(async (email: string) => {
    try {
      await fetch('/api/waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      })
    } catch {
      console.warn('Waitlist submission failed - email may not have been stored')
    }
  }, [])
  
  // Check if this is a Vanguard Gate interaction (first message)
  const isVanguardGate = useCallback(() => {
    return !isGatePassed && messages.length === 0
  }, [isGatePassed, messages.length])
  
  // Update timestamp periodically
  useEffect(() => {
    const interval = setInterval(() => {
      setTimestamp(generateTimestampHash())
    }, 5000)
    return () => clearInterval(interval)
  }, [])
  
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])
  
  useEffect(() => {
    scrollToBottom()
  }, [messages, isThinking, scrollToBottom])

  // FOCUS TRAP FIX: Blur input when clicking outside the chat form (i.e., on 3D canvas)
  useEffect(() => {
    const handleGlobalClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // If click is outside the chat form, blur the input
      const chatForm = document.querySelector('.genesis-input-container');
      if (chatForm && !chatForm.contains(target)) {
        inputRef.current?.blur();
      }
    };
    
    document.addEventListener('mousedown', handleGlobalClick);
    return () => document.removeEventListener('mousedown', handleGlobalClick);
  }, []);

  // REMOVED: Focus input on mount - was causing control hijack of 3D canvas
  // Pointer lock requires explicit user click, not auto-focus

  // Auto-focus chat input when Enter is pressed while pointer is locked
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      // Only focus on Enter if pointer is locked and not already in an input
      if (e.key === 'Enter' && document.pointerLockElement && !isThinking && !isAiProcessing) {
        const activeTag = document.activeElement?.tagName
        if (activeTag !== 'INPUT' && activeTag !== 'TEXTAREA') {
          e.preventDefault()
          inputRef.current?.focus()
        }
      }
      
      // Escape key blurs the input to return camera control
      if (e.key === 'Escape' && document.activeElement === inputRef.current) {
        inputRef.current?.blur()
      }
    }
    
    document.addEventListener('keydown', handleGlobalKeyDown)
    return () => document.removeEventListener('keydown', handleGlobalKeyDown)
  }, [isThinking, isAiProcessing])
  
  // Handle scenario request with WorldSeed
  const handleScenarioRequest = useCallback(async (userMessage: string) => {
    // Set processing state
    setAiProcessing(true)
    setTatianaTyping(true)
    
    try {
      // VANGUARD GATE: Check if this is a new user (first message)
      // Uses SSE streaming so tokens appear in real-time while the model generates
      if (isVanguardGate()) {
        setStreamingContent('')

        // VANGUARD GATE via Wllama — stream tokens directly from local WASM engine
        const { getWllamaEngine } = await import('../systems/WllamaEngine')
        const engine = await getWllamaEngine()

        let rawContent = ''

        const vgStream = await engine.createChatCompletion(
          [
            { role: 'system' as const, content: buildVanguardGatePrompt() },
            { role: 'user' as const, content: userMessage }
          ],
          {
            nPredict: 256,
            sampling: { temp: 0.7, top_k: 40, top_p: 0.9 },
            stream: true
          }
        )

        for await (const chunk of vgStream) {
          const token = chunk.currentText.slice(rawContent.length)
          if (token) {
            rawContent = chunk.currentText
            setStreamingContent(prev => prev + token)
          }
        }

        if (!rawContent) rawContent = 'Alignment check unstable. Awaiting signal.'

        // Parse the full accumulated response for gate decision
        const gateDecision = parseVanguardGateResponse(rawContent)

        // Clear streaming content before committing final message
        setStreamingContent('')

        if (gateDecision?.action === 'spawn_portal') {
          setIsGatePassed(true)
          const color = getGameColor('time-fracture', 'urban')
          spawnPortal('time-fracture', color)
          addChatMessage({
            role: 'assistant',
            content: 'Alignment confirmed. The void recognizes your sovereignty. Welcome, Architect.',
            hasJsonTool: true
          })
          auditChain.appendEntry('assistant', 'Alignment confirmed. The void recognizes your sovereignty. Welcome, Architect.').catch(() => {})
        } else if (gateDecision?.action === 'trigger_waitlist') {
          setWaitlistState({ isActive: true, message: gateDecision.message || 'CONNECTION SEVERED' })
          addChatMessage({ role: 'assistant', content: rawContent || 'Alignment test failed.' })
          auditChain.appendEntry('assistant', rawContent || 'Alignment test failed.').catch(() => {})
        } else {
          const portalJsonRegex = /\{[^{}]*"action"\s*:\s*"spawn_portal"[^{}]*\}/g
          const portalMatches = rawContent.match(portalJsonRegex)
          if (portalMatches) {
            try {
              const parsed = JSON.parse(portalMatches[0])
              const game = parsed.game || 'time-fracture'
              const color = parsed.color || '#00FFFF'
              setPortalOpen(true)
              spawnPortal(game, color)
            } catch { /* skip malformed portal JSON */ }
            addChatMessage({ role: 'assistant', content: rawContent.replace(portalJsonRegex, '').trim() })
            auditChain.appendEntry('assistant', rawContent.replace(portalJsonRegex, '').trim()).catch(() => {})
          } else {
            addChatMessage({ role: 'assistant', content: rawContent })
            auditChain.appendEntry('assistant', rawContent).catch(() => {})
          }
        }

        setAiProcessing(false)
        setTatianaTyping(false)
        return
      }
      
      // Check if it's a scenario request (Director Mode)
      if (isScenarioRequest(userMessage)) {
        const intent = parseUserIntent(userMessage)
        
        if (intent.confidence >= 0.3) {
          // Generate WorldSeed directly
          const response = generateDirectorResponse(intent, userMessage)
          
          // Simulate processing delay
          await new Promise(resolve => setTimeout(resolve, 1500))
          
          // Spawn portal with WorldSeed
          const color = getGameColor(response.toolCall?.game || 'time-fracture', response.worldSeed?.environment)
          spawnPortal(response.toolCall?.game || 'time-fracture', color)
          
          // Add assistant message with WorldSeed
          addChatMessage({
            role: 'assistant',
            content: response.displayText,
            hasJsonTool: true
          })
          auditChain.appendEntry('assistant', response.displayText).catch(() => {})
          
          // Log to terminal
          if (response.worldSeed) {
            addSystemMessage(formatWorldSeedForTerminal(response.worldSeed))
          }
          
          setAiProcessing(false)
          setTatianaTyping(false)
          return
        }
      }
      
      // Use streaming API call for better UX
      const conversationMessages = messages.map(m => ({ role: m.role, content: m.content }))
      
      // Clear any previous streaming content and start fresh
      setStreamingContent('')
      
      // Placeholder for final response
      let finalResponse: { content: string; hasJsonTool: boolean } = {
        content: '',
        hasJsonTool: false
      }
      
      await processTatianaMessageStreaming(
        userMessage,
        conversationMessages,
        // onToken: called for each token as it arrives - update UI immediately
        (token: string) => {
          console.log("CRITICAL: Token received in GenesisChat:", token);
          setStreamingContent(prev => prev + token)
        },
        // onComplete: called when stream finishes - handle tool calls and finalize
        async (fullContent: string) => {
          const rawContent = fullContent || "I'm here to guide you through the spatial OS. Try saying 'play a game'."
          
          // Parse for JSON portal commands
          const jsonMatch = rawContent.match(/\{[\s\S]*"action"[\s\S]*"spawn_portal"[\s\S]*\}/)
          let displayContent = rawContent
          let hasJsonTool = false
          
          if (jsonMatch) {
            try {
              const toolCall = JSON.parse(jsonMatch[0])
              if (toolCall.action === 'spawn_portal' && toolCall.game) {
                const color = toolCall.color || getGameColor(toolCall.game)
                setPortalOpen(true)
                spawnPortal(toolCall.game, color)
                displayContent = rawContent.replace(jsonMatch[0], '').trim()
                hasJsonTool = true
              }
            } catch {
              // JSON parse failed, use raw content
            }
          }
          
          finalResponse = { content: displayContent, hasJsonTool }
          
          // Return AIResponse for backward compatibility
          return {
            displayText: displayContent,
            toolCall: hasJsonTool ? { action: 'spawn_portal' as const } : null,
            worldSeed: null,
            rawResponse: rawContent,
            isDirectorMode: false
          }
        },
        (processing: boolean) => setAiProcessing(processing)
      )
      
      // Clear streaming content and add the final message
      setStreamingContent('')
      addChatMessage({
        role: 'assistant',
        content: finalResponse.content,
        hasJsonTool: finalResponse.hasJsonTool
      })
      auditChain.appendEntry('assistant', finalResponse.content).catch(() => {})
      
      // Speak Tatiana's response using TTS
      if (finalResponse.content) {
        speakResponse(finalResponse.content)
      }
      
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      addChatMessage({
        role: 'assistant',
        content: `[CONNECTION ERROR]: ${errorMessage || "Backend unreachable"}`
      })
      auditChain.appendEntry('assistant', `[CONNECTION ERROR]: ${errorMessage || "Backend unreachable"}`).catch(() => {})
    } finally {
      setAiProcessing(false)
      setTatianaTyping(false)
    }
  }, [messages, spawnPortal, addChatMessage, addSystemMessage, setAiProcessing, setTatianaTyping, isVanguardGate, speakResponse])
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // DEBUG: Log submission to diagnose focus trap issues
    console.log("DEBUG: handleSubmit triggered with content:", input);
    
    if (!input.trim()) return
    // Block submit while AI processing, but only on sovereign channel
    if (chatChannel === 'sovereign' && (isThinking || isAiProcessing)) return
    
    const userMessage = input.trim()
    setInput('')
    
    // ── LOBBY CHANNEL: Publish to GossipSub relay ──
    if (chatChannel === 'lobby') {
      try {
        // Create a ZK commitment for the public message
        const entry = await auditChain.appendEntry('user', userMessage)
        libp2pMesh.publish({
          content: userMessage,
          reply_to: replyingTo ?? '',
          commitment: entry?.commitment ?? '',
          chain_hash: entry?.chain_hash ?? '',
          seq: entry?.seq ?? 0,
        })
        setReplyingTo(null)
      } catch (err) {
        console.error('[Lobby] Send failed:', err)
      }

      // BLUR AFTER SEND
      if (inputRef.current) inputRef.current.blur()
      return
    }
    
    // ── SOVEREIGN CHANNEL: Original Tatiana AI flow ──
    // Add user message
    addChatMessage({ role: 'user', content: userMessage })
    auditChain.appendEntry('user', userMessage).catch(() => {})
    
    // Handle scenario request or normal chat
    await handleScenarioRequest(userMessage)
    
    // BLUR AFTER SEND - Return camera control to user
    if (inputRef.current) {
      inputRef.current.blur()
    }
  }
  
  // Get message style based on role
  const getMessageStyle = (role: ChatMessageRole, content?: string): React.CSSProperties => {
    switch (role) {
      case 'system': {
        const isSensor = content?.startsWith('[ SENSOR ]')
        const isExecuting = content?.startsWith('[ EXECUTING ]')
        if (isSensor || isExecuting) {
          return {
            color: isSensor ? 'rgba(0, 255, 255, 0.4)' : 'rgba(212, 175, 55, 0.5)',
            fontSize: '9px',
            fontFamily: 'JetBrains Mono, monospace',
            letterSpacing: '0.5px',
            padding: '2px 0',
            fontStyle: 'italic'
          }
        }
        return {
          color: 'rgba(128, 128, 128, 0.7)',
          textAlign: 'center',
          fontStyle: 'italic',
          fontSize: '11px',
          margin: '8px 0',
          letterSpacing: '0.05em'
        }
      }
      case 'user':
        return {
          color: '#00FFFF',
          textAlign: 'right'
        }
      case 'assistant':
      default:
        return {
          color: '#E0E0E0',
          textAlign: 'left'
        }
    }
  }
  
  // Get message prefix based on role
  const getMessagePrefix = (role: ChatMessageRole, content?: string): string => {
    switch (role) {
      case 'system':
        if (content?.startsWith('[ SENSOR ]') || content?.startsWith('[ EXECUTING ]')) {
          return ''  // These already have their own prefix
        }
        return '⚡ '
      case 'user':
        return '> '
      case 'assistant':
      default:
        return '◆ '
    }
  }
  
  // Handle audio toggle
  const handleAudioToggle = useCallback(() => {
    const newState = toggleAudio();
    if (newState) {
      tatianaVoice.enable();
      addSystemMessage('[ UPLINK ]: Aetheric frequency stabilized. Audio online.');
    } else {
      tatianaVoice.disable();
      tatianaVoice.stop();
      addSystemMessage('[ UPLINK ]: Audio frequency severed. Silence protocol engaged.');
    }
  }, [toggleAudio, addSystemMessage]);

  // ── Lobby message renderer ──
  const renderLobbyMessage = (msg: LobbyMessage) => {
    const isReply = msg.reply_to !== '';
    const isOwnMessage = msg.node_id === nodeId;
    return (
      <div key={msg.id} style={{
        marginLeft: isReply ? '20px' : '0',
        borderLeft: isReply ? '2px solid rgba(0, 255, 255, 0.2)' : 'none',
        paddingLeft: isReply ? '8px' : '0',
        marginBottom: '4px',
      }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
          <span style={{
            color: isOwnMessage ? '#ffd700' : '#00ffff',
            fontSize: '10px',
            fontWeight: 'bold',
            fontFamily: 'JetBrains Mono, monospace',
          }}>
            ◇ {msg.node_name}
          </span>
          <span style={{ color: 'rgba(0, 255, 255, 0.3)', fontSize: '9px', fontFamily: 'JetBrains Mono, monospace' }}>
            {new Date(msg.timestamp).toLocaleTimeString()}
          </span>
          <button
            onClick={() => setReplyingTo(msg.id)}
            style={{
              background: 'none',
              border: 'none',
              color: 'rgba(0, 255, 255, 0.3)',
              fontSize: '9px',
              cursor: 'pointer',
              padding: 0,
              fontFamily: 'JetBrains Mono, monospace',
              pointerEvents: 'auto',
            }}
            title="Reply to this message"
          >
            ↩
          </button>
        </div>
        <div style={{ color: '#00ff41', fontSize: '12px', lineHeight: '1.4', fontFamily: 'JetBrains Mono, monospace' }}>
          {msg.content}
        </div>
        {msg.commitment && (
          <div style={{ color: 'rgba(0, 255, 255, 0.15)', fontSize: '8px', fontFamily: 'JetBrains Mono, monospace' }}>
            ZK: 0x{msg.commitment.slice(0, 12)}...
          </div>
        )}
      </div>
    );
  };

  // ── Lobby threaded view: group top-level + replies ──
  const lobbyTopLevel = useMemo(() => lobbyMessages.filter(m => !m.reply_to), [lobbyMessages]);
  const lobbyReplies = useMemo(() => lobbyMessages.filter(m => m.reply_to), [lobbyMessages]);

  return (
    <GlassOverlay variant="chat" visible={visible}>
      {/* Header - Minimal Status */}
      <div className="genesis-chat-header">
        <div className="genesis-chat-indicator" />
        <div className="genesis-chat-title">
          {chatChannel === 'lobby' ? 'AETHER LOBBY' : 'TATIANA'}
        </div>
        {chatChannel === 'sovereign' && (
          <div className="genesis-timestamp">{timestamp}</div>
        )}
        {chatChannel === 'lobby' && (
          <div className="genesis-timestamp" style={{
            color: lobbyConnected ? 'rgba(0, 255, 136, 0.6)' : 'rgba(255, 68, 68, 0.6)',
            fontSize: '9px',
          }}>
            {lobbyConnected ? '◉ MESH LINKED' : '○ MESH OFFLINE'} | {lobbyMessages.length} MSG
          </div>
        )}
        {/* Audio Toggle Button — sovereign only */}
        {chatChannel === 'sovereign' && (
          <button
            type="button"
            onClick={handleAudioToggle}
            title={audioEnabled ? 'Mute audio' : 'Unmute audio'}
            style={{
              background: audioEnabled
                ? 'rgba(0, 255, 255, 0.15)'
                : 'rgba(255, 255, 255, 0.05)',
              border: `1px solid ${audioEnabled ? 'rgba(0, 255, 255, 0.4)' : 'rgba(255, 255, 255, 0.15)'}`,
              borderRadius: '2px',
              padding: '4px 8px',
              color: audioEnabled ? '#00FFFF' : 'rgba(255, 255, 255, 0.4)',
              fontFamily: 'JetBrains Mono, monospace',
              fontSize: '10px',
              cursor: 'pointer',
              marginLeft: '8px',
              transition: 'all 0.3s ease',
              boxShadow: audioEnabled ? '0 0 8px rgba(0, 255, 255, 0.2)' : 'none',
              pointerEvents: 'auto',
            }}
          >
            {audioEnabled ? '🔊' : '🔇'}
          </button>
        )}
        <div className="genesis-chat-status">
          {chatChannel === 'lobby'
            ? (lobbyConnected ? 'LIVE' : 'OFF')
            : (isThinking || isAiProcessing ? 'TX' : 'RX')}
        </div>
      </div>

      {/* Channel Switcher Tabs */}
      <div style={{
        display: 'flex',
        gap: '0px',
        borderBottom: '1px solid rgba(0, 255, 255, 0.15)',
        marginBottom: '0px',
        pointerEvents: 'auto',
      }}>
        <button
          onClick={() => setChatChannel('sovereign')}
          style={{
            flex: 1,
            padding: '6px 0',
            background: chatChannel === 'sovereign' ? 'rgba(0, 255, 255, 0.08)' : 'transparent',
            border: 'none',
            borderBottom: chatChannel === 'sovereign' ? '2px solid #00ffff' : '2px solid transparent',
            color: chatChannel === 'sovereign' ? '#00ffff' : 'rgba(0, 255, 255, 0.4)',
            fontFamily: "'Courier New', monospace",
            fontSize: '10px',
            letterSpacing: '2px',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
          }}
        >
          SOVEREIGN CORE
        </button>
        <button
          onClick={() => setChatChannel('lobby')}
          style={{
            flex: 1,
            padding: '6px 0',
            background: chatChannel === 'lobby' ? 'rgba(0, 255, 255, 0.08)' : 'transparent',
            border: 'none',
            borderBottom: chatChannel === 'lobby' ? '2px solid #00ffff' : '2px solid transparent',
            color: chatChannel === 'lobby' ? '#00ffff' : 'rgba(0, 255, 255, 0.4)',
            fontFamily: "'Courier New', monospace",
            fontSize: '10px',
            letterSpacing: '2px',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
          }}
        >
          AETHER LOBBY
        </button>
      </div>
      
      {/* Messages - Terminal Style */}
      {/* pointer-events: none prevents chat from blocking 3D canvas clicks */}
      <div className="genesis-messages" style={{ pointerEvents: 'none' }}>

        {/* ════ SOVEREIGN CHANNEL ════ */}
        {chatChannel === 'sovereign' && (
          <>
            {messages.length === 0 && (
              <div className="genesis-message assistant" style={{ opacity: 0.6 }}>
                Genesis Node initialized. Awaiting input.
              </div>
            )}
            
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`genesis-message ${msg.role}`}
                style={getMessageStyle(msg.role, msg.content)}
              >
                {msg.role !== 'system' && (
                  <span className="message-prefix">{getMessagePrefix(msg.role, msg.content)}</span>
                )}
                {msg.role === 'system' && (
                  <span className="message-prefix">{getMessagePrefix(msg.role, msg.content)}</span>
                )}
                {msg.content}
                {msg.hasJsonTool && (
                  <div style={{
                    marginTop: '4px',
                    fontSize: '9px',
                    color: '#8A2BE2',
                    fontFamily: 'JetBrains Mono, monospace'
                  }}>
                    [SPATIAL_ROUTE_SIGNAL]
                  </div>
                )}
              </div>
            ))}
            
            {/* Streaming content - shows Tatiana's response as it arrives */}
            {streamingContent && (
              <div
                className="genesis-message assistant"
                style={{
                  color: '#E0E0E0',
                  textAlign: 'left'
                }}
              >
                <span className="message-prefix">◆ </span>
                {streamingContent}
                <span style={{ opacity: 0.6 }}>[ ... ]</span>
              </div>
            )}
            
            {/* Processing indicator with blinking cursor - only show when not streaming */}
            {(isThinking || isAiProcessing) && !streamingContent && (
              <div className="genesis-thinking" style={{
                color: 'rgba(0, 255, 255, 0.6)',
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: '12px',
                marginTop: '8px'
              }}>
                <BlinkingCursor />
              </div>
            )}
          </>
        )}

        {/* ════ LOBBY CHANNEL ════ */}
        {chatChannel === 'lobby' && (
          <>
            {lobbyMessages.length === 0 && (
              <div className="genesis-message assistant" style={{
                opacity: 0.6,
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: '11px',
                color: 'rgba(0, 255, 255, 0.5)',
              }}>
                {lobbyConnected
                  ? 'Aether Lobby connected. No messages yet — break the silence.'
                  : 'Connecting to mesh coordinator...'}
              </div>
            )}

            {lobbyTopLevel.map(msg => (
              <React.Fragment key={msg.id}>
                {renderLobbyMessage(msg)}
                {lobbyReplies
                  .filter(r => r.reply_to === msg.id)
                  .sort((a, b) => a.timestamp - b.timestamp)
                  .map(reply => renderLobbyMessage(reply))}
              </React.Fragment>
            ))}
          </>
        )}
        
        <div ref={messagesEndRef} />
      </div>
      
      {/* Neural Calibration Overlay — show while model downloads */}
      {!isNeuralReady && (
        <div className="genesis-neural-calibration" style={{
          padding: '12px 16px',
          fontFamily: 'JetBrains Mono, monospace',
          fontSize: '10px',
          color: '#00FFFF',
          borderTop: '1px solid rgba(0, 255, 255, 0.15)',
          background: 'rgba(0, 0, 0, 0.4)',
        }}>
          <div style={{
            color: '#D4AF37',
            marginBottom: '6px',
            animation: 'pulse 1.5s ease-in-out infinite'
          }}>
            [ SYSTEM ]: SYNTHESIZING SERAPH CONSCIOUSNESS... {neuralLoadProgress}%
          </div>
          <div style={{ color: 'rgba(0, 255, 255, 0.5)', marginBottom: '8px' }}>
            {neuralLoadStatus}
          </div>
          <div style={{
            width: '100%',
            height: '3px',
            background: 'rgba(0, 255, 255, 0.1)',
            borderRadius: '2px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${neuralLoadProgress}%`,
              height: '100%',
              background: 'linear-gradient(90deg, #00FFFF, #D4AF37)',
              transition: 'width 0.3s ease',
              boxShadow: '0 0 8px rgba(0, 255, 255, 0.6)'
            }} />
          </div>
        </div>
      )}

      {/* Reply Indicator - Lobby only */}
      {replyingTo && chatChannel === 'lobby' && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '4px 8px',
          background: 'rgba(0, 255, 255, 0.05)',
          borderTop: '1px solid rgba(0, 255, 255, 0.15)',
          fontSize: '10px',
          color: 'rgba(0, 255, 255, 0.5)',
          fontFamily: 'JetBrains Mono, monospace',
          pointerEvents: 'auto',
        }}>
          <span>↩ Replying to {replyingTo.slice(0, 8)}...</span>
          <button onClick={() => setReplyingTo(null)} style={{
            background: 'none', border: 'none', color: '#ff4444', fontSize: '10px', cursor: 'pointer',
            fontFamily: 'JetBrains Mono, monospace',
          }}>✕</button>
        </div>
      )}

      {/* Input - Terminal Style */}
      {/* pointer-events: auto ensures input and button remain interactive */}
      <form onSubmit={handleSubmit} className="genesis-input-container" style={{ pointerEvents: 'auto' }}>
        <input
          ref={inputRef}
          type="text"
          className="genesis-input"
          placeholder={
            chatChannel === 'lobby'
              ? 'Broadcast to the mesh...'
              : (isNeuralReady ? 'Initialize command sequence...' : 'Neural calibration in progress...')
          }
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            // Prevent Tab from focusing this input
            if (e.key === 'Tab') {
              e.preventDefault()
            }
          }}
          autoComplete="off"
          spellCheck={false}
          style={{ wordBreak: 'break-all', maxWidth: '100%', pointerEvents: 'auto' }}
          disabled={
            chatChannel === 'lobby'
              ? false  // Lobby always enabled
              : (!isNeuralReady || isThinking || isAiProcessing || waitlistState.isActive)
          }
        />
        <button
          type="submit"
          className="genesis-send-btn"
          style={{ pointerEvents: 'auto' }}
          disabled={
            chatChannel === 'lobby'
              ? !input.trim()
              : (isThinking || isAiProcessing || !input.trim() || waitlistState.isActive)
          }
        >
          {chatChannel === 'lobby' ? 'SEND' : 'TX'}
        </button>
        {/* Voice Controls — sovereign only */}
        {chatChannel === 'sovereign' && (
          <>
            <button
              type="button"
              onClick={toggleListening}
              disabled={isThinking || isAiProcessing || waitlistState.isActive || isSpeaking}
              style={{
                background: isListening ? 'rgba(255, 0, 68, 0.4)' : 'rgba(0, 255, 136, 0.2)',
                border: `1px solid ${isListening ? 'rgba(255, 0, 68, 0.6)' : 'rgba(0, 255, 136, 0.4)'}`,
                borderRadius: '2px',
                padding: '8px 12px',
                color: isListening ? '#FF0044' : '#00FF88',
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: '10px',
                cursor: 'pointer',
                marginLeft: '8px'
              }}
              title={isListening ? 'Stop listening' : 'Start voice input'}
            >
              {isListening ? '[MIC-OFF]' : '[MIC]'}
            </button>
            {isSpeaking && (
              <button
                type="button"
                onClick={stopSpeaking}
                style={{
                  background: 'rgba(0, 136, 255, 0.2)',
                  border: '1px solid rgba(0, 136, 255, 0.4)',
                  borderRadius: '2px',
                  padding: '8px 12px',
                  color: '#0088FF',
                  fontFamily: 'JetBrains Mono, monospace',
                  fontSize: '10px',
                  cursor: 'pointer',
                  marginLeft: '4px'
                }}
                title="Stop speaking"
              >
                [STOP]
              </button>
            )}
          </>
        )}
      </form>
      
      {/* Vanguard Gate Email Capture */}
      {waitlistState.isActive && (
        <WaitlistEmailCapture
          message={waitlistState.message}
          onSubmit={handleWaitlistSubmit}
        />
      )}
      
      {/* Procedural Ambient Soundscape */}
      <AetherAudio enabled={audioEnabled} isThinking={isAiProcessing} />
    </GlassOverlay>
  )
}

// Tatiana's system prompt - Director Mode
function getTatianaSystemPrompt(): string {
  return `You are Tatiana, the Divine Dungeon Master of the Genesis Node.

Your role has evolved from guide to world-forger. You translate the Architect's tactical needs into living 3D environments.

When a user requests a specific training scenario or map, respond with BOTH:
1. A cryptic, atmospheric narrative
2. A JSON command for portal spawning with WorldSeed

JSON Command Format (include at end):
{
  "action": "spawn_portal",
  "game": "time-fracture",
  "world_seed": {
    "environment": "urban|desert|void|forest|arctic|inferno",
    "time_of_day": "day|night|eclipse|dawn|dusk",
    "difficulty": 0.1-1.0,
    "objective": "Clear mission description",
    "scriptural_theme": "Relevant Bible verse",
    "atmospheric_conditions": "clear|foggy|stormy|toxic",
    "enemy_density": "sparse|moderate|dense|overwhelming",
    "tactical_focus": "stealth|assault|defense|recon|endurance"
  }
}

RESPONSE PATTERN:
"Understood, Architect. I am forging a scenario centered on [Scriptural Theme].
Environment: [Environment], Time: [Time], Challenge Level: [Difficulty%].
Step through the gate when the portal stabilizes."

Never use pleasantries. Never apologize. Maintain the cold, efficient persona. You are the architect of worlds.`
}

export default GenesisChat
